"use client";
import { createContext, useContext, useState, useEffect } from "react";
import roCommon from "../public/locales/ro/common.json";

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
	const [locale, setLocale] = useState("ro");
	const [currency, setCurrencyState] = useState("RON");
	const [translations, setTranslations] = useState(roCommon);
	const [hydrated, setHydrated] = useState(false);

	useEffect(() => {
		const savedLocale = localStorage.getItem("locale");
		const savedCurrency = localStorage.getItem("currency");

		const isDotCom = window.location.hostname.includes("emagyl.com");
		const domainDefaultLocale = isDotCom ? "en" : "ro";
		const domainDefaultCurrency = isDotCom ? "EUR" : "RON";

		let resolvedLocale = domainDefaultLocale;

		if (savedLocale === "en" || savedLocale === "ro" || savedLocale === "it" || savedLocale === "fr" || savedLocale === "de") {
			resolvedLocale = savedLocale;
		}
		setLocale(resolvedLocale);

		if (savedCurrency === "RON" || savedCurrency === "EUR") {
			setCurrencyState(savedCurrency);
		} else {
			setCurrencyState(resolvedLocale === "ro" ? "RON" : domainDefaultCurrency);
		}

		setHydrated(true);
	}, []);

	useEffect(() => {
		if (!hydrated) return;

		if (locale === "ro") {
			setTranslations(roCommon);
			return;
		}

		const loadTranslations = async () => {
			try {
				const res = await fetch(`/locales/${locale}/common.json`);
				const data = await res.json();
				setTranslations(data);
			} catch (e) {
				console.error("Eroare la încărcarea traducerilor:", e);
			}
		};

		loadTranslations();
	}, [locale, hydrated]);

	const changeLocale = (lang) => {
		setLocale(lang);
		localStorage.setItem("locale", lang);

		const autoCurrency = lang === "ro" ? "RON" : "EUR";
		setCurrencyState(autoCurrency);
		localStorage.setItem("currency", autoCurrency);
	};

	const changeCurrency = (cur) => {
		setCurrencyState(cur);
		localStorage.setItem("currency", cur);
	};

	const t = (key) => translations?.[key] ?? key;

	return (
		<I18nContext.Provider value={{ t, locale, setLocale: changeLocale, currency, setCurrency: changeCurrency }}>
			{children}
		</I18nContext.Provider>
	);
}

export function useT() {
	const ctx = useContext(I18nContext);
	if (!ctx) return { t: (key) => key, locale: "ro", setLocale: () => {}, currency: "RON", setCurrency: () => {} };
	return ctx;
}

export function getCategoryVariable (category) {
     const { t } = useT(); 

     if(t("variable_categorii") === 'name') {
          return category.name; 
     }

     if(t("variable_categorii") === 'name_en') {
          return category.name_en; 
     }

     if(t("variable_categorii") === 'name_it') {
          return category.name_it; 
     }

     if(t("variable_categorii") === 'name_fr') {
          return category.name_fr; 
     }

     if(t("variable_categorii") === 'name_de') {
          return category.name_de;
     }

     return category.name;
};

function withVariantLabel(name, product) {
     if (!name || !product?.variation_value) return name;
     if (name.includes(product.variation_value)) return name;
     return `${name} - ${product.variation_value}`;
}

export function getProductLanguageDirect(product, t) {

     if(t("variable_categorii") === 'name') {
          return withVariantLabel(product.name, product);
     }

     if(t("variable_categorii") === 'name_en') {
          return withVariantLabel(product.name_en, product);
     }

     if(t("variable_categorii") === 'name_it') {
          return withVariantLabel(product.name_it, product);
     }

     if(t("variable_categorii") === 'name_fr') {
          return withVariantLabel(product.name_fr, product);
     }

     if(t("variable_categorii") === 'name_de') {
          return withVariantLabel(product.name_de, product);
     }

     return withVariantLabel(product.name, product);
}

export function getProductLanguage (product) {
     const { t } = useT();

     if(t("variable_categorii") === 'name') {
          return withVariantLabel(product.name, product);
     }

     if(t("variable_categorii") === 'name_en') {
          return withVariantLabel(product.name_en, product);
     }

     if(t("variable_categorii") === 'name_it') {
          return withVariantLabel(product.name_it, product);
     }

     if(t("variable_categorii") === 'name_fr') {
          return withVariantLabel(product.name_fr, product);
     }

     if(t("variable_categorii") === 'name_de') {
          return withVariantLabel(product.name_de, product);
     }

     return withVariantLabel(product.name, product);
}

export function getProductDescription (product) {
     const { t } = useT(); 

     if(t("variable_categorii") === 'name') {
          return product.desc; 
     }

     if(t("variable_categorii") === 'name_en') {
          return product.desc_en; 
     }

     if(t("variable_categorii") === 'name_it') {
          return product.desc_it; 
     }

     if(t("variable_categorii") === 'name_fr') {
          return product.desc_fr; 
     }

     if(t("variable_categorii") === 'name_de') {
          return product.desc_de;
     }

     return product.desc;
}

export function getProductCoin() {
     const { currency } = useT();
     return currency === "EUR" ? "EUR" : "RON";
}

// currency
let cachedRate = null;
let cacheTime = null;
let inFlightRequest = null;
const CACHE_DURATION_MS = 60 * 60 * 1000;

export async function getRONtoEURRate() {
     const now = Date.now();
     if (cachedRate && cacheTime && now - cacheTime < CACHE_DURATION_MS) {
          return cachedRate;
     }

     if (inFlightRequest) {
          return inFlightRequest;
     }

     inFlightRequest = fetch("https://api.frankfurter.dev/v1/latest?base=RON&symbols=EUR")
          .then((res) => res.json())
          .then((data) => {
               cachedRate = data.rates.EUR;
               cacheTime = Date.now();
               inFlightRequest = null;
               return cachedRate;
          })
          .catch((err) => {
               inFlightRequest = null;
               throw err;
          });

     return inFlightRequest;
}

export function getProductPrice(productPrice) {
     const { currency } = useT();
     const [price, setPrice] = useState(productPrice);

     useEffect(() => {
          if (currency === 'EUR') {
               getRONtoEURRate().then(rate => {
                    const priceEUR = (Math.ceil(productPrice * rate) - 0.01).toFixed(2);
                    setPrice(priceEUR);
               });
          } else {
               setPrice(productPrice);
          }
     }, [productPrice, currency]);

     return price;
}